import { useEffect, useMemo, useState } from "react";
import { Input, Modal, Select, Toggle } from "../../../shared";
import {
  useAddTemplateMutation,
  useUpdateTemplateMutation,
} from "../../../redux/services/templates/api";
import { useGetTaskTemplateGroupsQuery } from "../../../redux/services/tasks/tasksApi";
import { getProductTerminology } from "../../../constants/product-terminology";
import { toast } from "react-toastify";
import { useConfig } from "../../../hooks";
import { ClientsDropdown } from "../../../components/ClientsDropdown";
import LocationDropdown from "../../../components/LocationDropdown";
import {
  LEG_STATUS_OPTIONS,
  LOAD_TYPE_OPTIONS,
  MILEAGE_SELECTION_OPTIONS,
  TEMPLATE_TYPES,
  TEMPLATE_FORM_SIZE,
} from "./template.constants";
import {
  getOperationalTemplateProfile,
  TRUCKING_LEG_TEMPLATE_TOGGLES,
} from "./template.profile";
import { buildTemplatePayload, getTemplateContent } from "./template.utils";

const defaultForm = {
  name: "",
  description: "",
  client: null,
  origin: null,
  destination: null,
  loadType: "Regular",
  status: "pending",
  weight: "",
  appointmentTime: "",
  useLegFlatRate: false,
  baseCharge: "",
  fuelSurcharge: "",
  dropCharge: "",
  otherCharges: "",
  totalMiles: "",
  defaultMileage: "",
  customMileage: "",
  billedMiles: "",
  mileageSelectionType: "defaultMileage",
  useCustomMileage: false,
  useCustomBaseCharge: false,
  useCustomFuelSurcharge: false,
  useCustomDropCharge: false,
  useCustomOtherCharges: false,
  hasHandoff: false,
  isHandoff: false,
};

const LegTemplateModal = ({ visible, template, onClose, mode = "create" }) => {
  const { appType } = useConfig();
  const terms = getProductTerminology(appType);
  const profile = getOperationalTemplateProfile(appType);
  const [form, setForm] = useState(defaultForm);
  const [selectedGroups, setSelectedGroups] = useState([]);

  const { data: groups = [] } = useGetTaskTemplateGroupsQuery({
    appliesTo: "leg",
  });
  const [addTemplate, { isLoading: isAdding }] = useAddTemplateMutation();
  const [updateTemplate, { isLoading: isUpdating }] =
    useUpdateTemplateMutation();

  const isLoading = isAdding || isUpdating;
  const isEdit = mode === "edit" && template?._id;

  useEffect(() => {
    if (!visible) return;

    const content = getTemplateContent(template);
    const defaults = content.defaults || content;

    setForm({
      ...defaultForm,
      name:
        mode === "duplicate" && template?.name
          ? `Copy of ${template.name}`
          : template?.name || "",
      description: template?.description || "",
      client: defaults.client || null,
      origin: defaults.origin || null,
      destination: defaults.destination || null,
      loadType: defaults.loadType || "Regular",
      status: defaults.status || "pending",
      weight: defaults.weight || "",
      appointmentTime: defaults.appointmentTime || "",
      useLegFlatRate: !!defaults.useLegFlatRate,
      baseCharge: defaults.baseCharge || "",
      fuelSurcharge: defaults.fuelSurcharge || "",
      dropCharge: defaults.dropCharge || "",
      otherCharges: defaults.otherCharges || "",
      totalMiles: defaults.totalMiles || "",
      defaultMileage: defaults.defaultMileage || "",
      customMileage: defaults.customMileage || "",
      billedMiles: defaults.billedMiles || "",
      mileageSelectionType:
        defaults.mileageSelectionType || "defaultMileage",
      useCustomMileage: !!defaults.useCustomMileage,
      useCustomBaseCharge: !!defaults.useCustomBaseCharge,
      useCustomFuelSurcharge: !!defaults.useCustomFuelSurcharge,
      useCustomDropCharge: !!defaults.useCustomDropCharge,
      useCustomOtherCharges: !!defaults.useCustomOtherCharges,
      hasHandoff: !!defaults.hasHandoff,
      isHandoff: !!defaults.isHandoff,
    });
    setSelectedGroups(content.taskTemplateGroups || []);
  }, [visible, template, mode]);

  const groupOptions = useMemo(
    () =>
      groups.filter((group) => {
        const appliesToMatch = ["leg", "both"].includes(
          group.appliesTo || "both",
        );
        const appTypeMatch =
          !group.appType || group.appType === "all" || group.appType === appType;
        return appliesToMatch && appTypeMatch;
      }),
    [groups, appType],
  );

  const legToggleFields = profile.leg.includesHandoffFlags
    ? TRUCKING_LEG_TEMPLATE_TOGGLES
    : TRUCKING_LEG_TEMPLATE_TOGGLES.filter(
        ([id]) => !["hasHandoff", "isHandoff"].includes(id),
      );

  const handleChange = (id, value) => {
    setForm((prev) => ({ ...prev, [id]: value }));
  };

  const handleLocationChange = (field, location) => {
    setForm((prev) => ({ ...prev, [field]: location }));
  };

  const handleToggleGroup = (groupKey) => {
    setSelectedGroups((prev) =>
      prev.includes(groupKey)
        ? prev.filter((key) => key !== groupKey)
        : [...prev, groupKey],
    );
  };

  const handleSubmit = async () => {
    if (!form.name?.trim()) {
      toast.error("Template name is required");
      return;
    }

    const defaults = {
      client: form.client,
      origin: form.origin,
      destination: form.destination,
      loadType: form.loadType,
      status: form.status,
      weight: Number(form.weight) || 0,
      appointmentTime: form.appointmentTime,
      useLegFlatRate: form.useLegFlatRate,
      baseCharge: Number(form.baseCharge) || 0,
      fuelSurcharge: Number(form.fuelSurcharge) || 0,
      dropCharge: Number(form.dropCharge) || 0,
      otherCharges: Number(form.otherCharges) || 0,
      totalMiles: Number(form.totalMiles) || 0,
      defaultMileage: Number(form.defaultMileage) || 0,
      customMileage: Number(form.customMileage) || null,
      billedMiles: Number(form.billedMiles) || null,
      mileageSelectionType: form.mileageSelectionType,
      useCustomMileage: form.useCustomMileage,
      useCustomBaseCharge: form.useCustomBaseCharge,
      useCustomFuelSurcharge: form.useCustomFuelSurcharge,
      useCustomDropCharge: form.useCustomDropCharge,
      useCustomOtherCharges: form.useCustomOtherCharges,
      hasHandoff: form.hasHandoff,
      isHandoff: form.isHandoff,
    };

    const payload = buildTemplatePayload({
      type: TEMPLATE_TYPES.LEG,
      form,
      defaults,
      taskTemplateGroups: selectedGroups,
      appType,
    });

    try {
      const response = isEdit
        ? await updateTemplate({ id: template._id, data: payload }).unwrap()
        : await addTemplate(payload).unwrap();

      if (response.status !== "success") {
        toast.error("Failed to save template");
        return;
      }

      toast.success("Template saved successfully");
      onClose?.();
    } catch (error) {
      console.error("Save leg template error:", error);
      toast.error(error.data?.message || "Failed to save template");
    }
  };

  return (
    <Modal
      title={
        isEdit
          ? `Edit ${terms.leg} Template`
          : `Create ${terms.leg} Template`
      }
      visible={visible}
      onClose={onClose}
      confirm={handleSubmit}
      loading={isLoading}
      size="lg"
      submitText="Save Template"
    >
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Template Name"
            value={form.name}
            onChange={(e) => handleChange("name", e.value)}
            placeholder={terms.legTemplatePlaceholder}
            required
            size={TEMPLATE_FORM_SIZE}
          />
          <Input
            label="Description"
            value={form.description}
            onChange={(e) => handleChange("description", e.value)}
            placeholder="When this template should be used"
            size={TEMPLATE_FORM_SIZE}
          />
        </div>

        {profile.leg.includesRouteDefaults && (
          <div className="space-y-4 rounded-lg border border-gray-200 p-4">
            <div>
              <h3 className="text-sm font-semibold text-neutral-700 uppercase tracking-wide">
                {terms.routeDefaultsSection}
              </h3>
              <p className="text-xs text-gray-500 mt-1">{terms.routeDefaultsHint}</p>
            </div>

            <ClientsDropdown
              id="leg-template-client"
              label="Default Client"
              placeholder="Search clients for route defaults"
              required={false}
              size={TEMPLATE_FORM_SIZE}
              form={{ client: form.client }}
              updateForm={({ id, value }) => {
                if (id === "client") {
                  setForm((prev) => ({
                    ...prev,
                    client: value,
                    origin: null,
                    destination: null,
                  }));
                }
              }}
              value={form.client}
              hideFooter
              onClick={(client) =>
                setForm((prev) => ({
                  ...prev,
                  client,
                  origin: null,
                  destination: null,
                }))
              }
              onChange={(client) =>
                setForm((prev) => ({
                  ...prev,
                  client,
                  origin: null,
                  destination: null,
                }))
              }
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <LocationDropdown
                id="origin"
                label={terms.originLabel}
                clientId={form.client?._id}
                value={form.origin?.address?.fullAddress || ""}
                onChange={(location) => handleLocationChange("origin", location)}
                placeholder={
                  form.client?._id
                    ? "Select default origin"
                    : "Select a client first"
                }
                size={TEMPLATE_FORM_SIZE}
              />
              <LocationDropdown
                id="destination"
                label={terms.destinationLabel}
                clientId={form.client?._id}
                value={form.destination?.address?.fullAddress || ""}
                onChange={(location) =>
                  handleLocationChange("destination", location)
                }
                placeholder={
                  form.client?._id
                    ? "Select default destination"
                    : "Select a client first"
                }
                size={TEMPLATE_FORM_SIZE}
              />
            </div>
          </div>
        )}

        <div className="space-y-4 rounded-lg border border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-neutral-700 uppercase tracking-wide border-b border-neutral-300 pb-2">
            {terms.legInformationSection}
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {profile.leg.includesLoadType && (
              <Select
                label={terms.loadTypeLabel}
                value={form.loadType}
                options={LOAD_TYPE_OPTIONS}
                onChange={(e) => handleChange("loadType", e.value)}
                size={TEMPLATE_FORM_SIZE}
              />
            )}
            <Select
              label="Status"
              value={form.status}
              options={LEG_STATUS_OPTIONS}
              onChange={(e) => handleChange("status", e.value)}
              size={TEMPLATE_FORM_SIZE}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {profile.leg.includesWeight && (
              <Input
                label="Weight"
                type="number"
                value={form.weight}
                onChange={(e) => handleChange("weight", e.value)}
                size={TEMPLATE_FORM_SIZE}
              />
            )}
            <Input
              label="Appointment Time"
              value={form.appointmentTime}
              onChange={(e) => handleChange("appointmentTime", e.value)}
              placeholder="8:00 AM"
              size={TEMPLATE_FORM_SIZE}
            />
            {profile.leg.includesMileageControls && (
              <>
                <Select
                  label="Mileage Type"
                  value={form.mileageSelectionType}
                  options={MILEAGE_SELECTION_OPTIONS}
                  onChange={(e) =>
                    handleChange("mileageSelectionType", e.value)
                  }
                  size={TEMPLATE_FORM_SIZE}
                />
                <Input
                  label="Total Miles"
                  type="number"
                  value={form.totalMiles}
                  onChange={(e) => handleChange("totalMiles", e.value)}
                  size={TEMPLATE_FORM_SIZE}
                />
              </>
            )}
          </div>

          {profile.leg.includesMileageControls && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {form.mileageSelectionType === "defaultMileage" && (
                <Input
                  label="Default Mileage"
                  type="number"
                  value={form.defaultMileage}
                  onChange={(e) => handleChange("defaultMileage", e.value)}
                  size={TEMPLATE_FORM_SIZE}
                />
              )}
              {form.mileageSelectionType === "billedMileage" && (
                <Input
                  label="Billed Miles"
                  type="number"
                  value={form.billedMiles}
                  onChange={(e) => handleChange("billedMiles", e.value)}
                  size={TEMPLATE_FORM_SIZE}
                />
              )}
              {form.mileageSelectionType === "customInput" && (
                <Input
                  label="Custom Mileage"
                  type="number"
                  value={form.customMileage}
                  onChange={(e) => handleChange("customMileage", e.value)}
                  size={TEMPLATE_FORM_SIZE}
                />
              )}
            </div>
          )}

          {profile.leg.includesCharges && (
            <>
              <div className="flex items-center justify-between border-t border-neutral-200 pt-4">
                <span className="text-sm font-medium text-neutral-700">
                  Use Leg Flat Rate
                </span>
                <Toggle
                  id="useLegFlatRate"
                  size={TEMPLATE_FORM_SIZE}
                  checked={form.useLegFlatRate || false}
                  onChange={(e) =>
                    handleChange("useLegFlatRate", e.target.checked)
                  }
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Input
                  label="Base Charge"
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.baseCharge}
                  onChange={(e) => handleChange("baseCharge", e.value)}
                  size={TEMPLATE_FORM_SIZE}
                />
                <Input
                  label="Fuel Surcharge"
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.fuelSurcharge}
                  onChange={(e) => handleChange("fuelSurcharge", e.value)}
                  size={TEMPLATE_FORM_SIZE}
                />
                <Input
                  label="Drop Charge"
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.dropCharge}
                  onChange={(e) => handleChange("dropCharge", e.value)}
                  size={TEMPLATE_FORM_SIZE}
                />
                <Input
                  label="Other Charges"
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.otherCharges}
                  onChange={(e) => handleChange("otherCharges", e.value)}
                  size={TEMPLATE_FORM_SIZE}
                />
              </div>
            </>
          )}

          {legToggleFields.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {legToggleFields.map(([id, label]) => (
                <label
                  key={id}
                  className="flex items-center gap-2 text-sm text-gray-700"
                >
                  <input
                    type="checkbox"
                    checked={!!form[id]}
                    onChange={(e) => handleChange(id, e.target.checked)}
                  />
                  {label}
                </label>
              ))}
            </div>
          )}
        </div>

        {groupOptions.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-gray-700">
              Task/Checklist Groups
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {groupOptions.map((group) => (
                <label
                  key={group._id}
                  className="flex items-center gap-2 text-sm"
                >
                  <input
                    type="checkbox"
                    checked={selectedGroups.includes(group.groupKey)}
                    onChange={() => handleToggleGroup(group.groupKey)}
                  />
                  {group.title}
                </label>
              ))}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default LegTemplateModal;
